/**
 * Fatturato netto (barre) e margine netto (linea) nel tempo. I colori si
 * leggono dalle variabili del tema: recharts li scrive come attributi SVG,
 * dove ``var(--om-*)`` non funziona.
 */

import { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "@/shared/useTheme";
import type { BiGiorno } from "../biApi";
import { eur, intero, marginePerc, n, perc, raggruppa, type Grana } from "../biTesti";
import { Pillole, Riquadro } from "./Riquadro";

function colore(nome: string): string {
  if (typeof window === "undefined") return "#000";
  const v = getComputedStyle(document.documentElement).getPropertyValue(`--om-${nome}`).trim();
  return v ? `rgb(${v.split(/\s+/).join(",")})` : "#000";
}

function useColori() {
  const { theme } = useTheme();
  return useMemo(
    () => ({
      ink: colore("ink"),
      line: colore("line"),
      accent: colore("accent"),
      soft: colore("accent-soft"),
      green: colore("green"),
      card: colore("card"),
      lineaForte: colore("line-2"),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i colori cambiano col tema
    [theme],
  );
}

function etichettaData(iso: string, grana: Grana): string {
  const d = new Date(`${iso}T00:00:00`);
  if (grana === "mese") return d.toLocaleDateString("it-IT", { month: "short", year: "2-digit" });
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
}

function Fumetto({
  active,
  payload,
  grana,
}: {
  active?: boolean;
  payload?: { payload: BiGiorno }[];
  grana: Grana;
}) {
  const r = payload?.[0]?.payload;
  if (!active || !r) return null;
  const titolo =
    grana === "settimana"
      ? `Settimana dal ${etichettaData(r.day, "giorno")}`
      : etichettaData(r.day, grana === "mese" ? "mese" : "giorno");
  return (
    <div className="rounded-[12px] border border-line-2 bg-card px-3 py-2.5 text-[12.5px] text-ink shadow-[0_12px_30px_-14px_rgba(31,27,22,0.4)]">
      <p className="mb-1 font-bold">{titolo}</p>
      <p className="tabular-nums">
        Ordini: <b>{intero(r.orders)}</b>
      </p>
      <p className="tabular-nums">
        Fatturato netto: <b>{eur(r.net_revenue)}</b>
      </p>
      <p className="tabular-nums">
        Margine netto: <b>{eur(r.margin)}</b> ({perc(marginePerc(r))})
      </p>
      {(n(r.estimate_count) > 0 || n(r.not_computable_count) > 0) && (
        <p className="mt-1 tabular-nums">
          ≈ {intero(r.estimate_count)} stimati · {intero(r.not_computable_count)} senza margine
        </p>
      )}
    </div>
  );
}

export function Andamento({ giorni }: { giorni: BiGiorno[] }) {
  const [grana, setGrana] = useState<Grana>(giorni.length > 120 ? "settimana" : "giorno");
  const c = useColori();
  const dati = useMemo(
    () =>
      raggruppa(giorni, grana).map((g) => ({
        ...g,
        net_revenue: n(g.net_revenue),
        margin: n(g.margin),
      })),
    [giorni, grana],
  );

  return (
    <Riquadro
      titolo="Andamento"
      spiegazione={{
        titolo: "Andamento",
        testo:
          "Barre: fatturato netto di ogni giorno (o settimana, o mese). Linea: margine netto degli ordini calcolabili. Il giorno e' quello dell'ordine, in ora italiana. Passa sopra a una barra per vedere i numeri.",
      }}
      azioni={
        <Pillole
          etichetta="Raggruppa per"
          valore={grana}
          onChange={setGrana}
          opzioni={[
            { id: "giorno", nome: "Giorno" },
            { id: "settimana", nome: "Settimana" },
            { id: "mese", nome: "Mese" },
          ]}
        />
      }
    >
      <div className="mb-2 flex flex-wrap gap-4 text-[12.5px] font-semibold">
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2.5 w-3.5 rounded-[3px] bg-accent/40" />
          Fatturato netto
        </span>
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" className="h-[3px] w-4 rounded-full bg-ink" />
          Margine netto
        </span>
      </div>
      {dati.length === 0 ? (
        <p className="py-16 text-center text-[13px]">Nessun ordine nel periodo.</p>
      ) : (
        <div className="h-[300px] max-md:h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={dati} margin={{ top: 6, right: 4, bottom: 0, left: 4 }}>
              <CartesianGrid stroke={c.line} vertical={false} />
              <XAxis
                dataKey="day"
                tickFormatter={(v: string) => etichettaData(v, grana)}
                tick={{ fontSize: 11, fill: c.ink }}
                tickLine={false}
                axisLine={{ stroke: c.lineaForte }}
                minTickGap={22}
              />
              <YAxis
                tickFormatter={(v: number) => eur(v)}
                tick={{ fontSize: 11, fill: c.ink }}
                tickLine={false}
                axisLine={false}
                width={78}
              />
              <Tooltip
                content={<Fumetto grana={grana} />}
                cursor={{ fill: c.soft, opacity: 0.5 }}
              />
              <Bar
                dataKey="net_revenue"
                fill={c.accent}
                fillOpacity={0.35}
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              />
              <Line
                dataKey="margin"
                type="monotone"
                stroke={c.ink}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: c.ink, stroke: c.card, strokeWidth: 2 }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Riquadro>
  );
}
